import { BubbleMenu } from '@tiptap/react/menus'
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  PanelTop,
  Trash2,
} from 'lucide-react'
import type { Editor } from '@tiptap/core'
import type { LucideIcon } from 'lucide-react'

/**
 * Row/column controls for a table, shown as a bubble menu anchored to the
 * cell the caret sits in.
 *
 * Deliberately shows only when the text selection is *empty*: selecting text
 * inside a cell should still get the formatting toolbar, and two bubble menus
 * competing for the same anchor point overlap badly. Caret in a cell → table
 * controls; text selected → formatting controls.
 */

interface TableToolbarProps {
  editor: Editor
}

interface TableAction {
  key: string
  label: string
  Icon: LucideIcon
  run: (editor: Editor) => void
  destructive?: boolean
}

const ACTIONS: Array<TableAction> = [
  {
    key: 'add-row-before',
    label: 'Insert row above',
    Icon: ArrowUp,
    run: (editor) => {
      editor.chain().focus().addRowBefore().run()
    },
  },
  {
    key: 'add-row-after',
    label: 'Insert row below',
    Icon: ArrowDown,
    run: (editor) => {
      editor.chain().focus().addRowAfter().run()
    },
  },
  {
    key: 'add-column-before',
    label: 'Insert column left',
    Icon: ArrowLeft,
    run: (editor) => {
      editor.chain().focus().addColumnBefore().run()
    },
  },
  {
    key: 'add-column-after',
    label: 'Insert column right',
    Icon: ArrowRight,
    run: (editor) => {
      editor.chain().focus().addColumnAfter().run()
    },
  },
  {
    key: 'toggle-header-row',
    label: 'Toggle header row',
    Icon: PanelTop,
    run: (editor) => {
      editor.chain().focus().toggleHeaderRow().run()
    },
  },
]

const DESTRUCTIVE_ACTIONS: Array<TableAction> = [
  {
    key: 'delete-row',
    label: 'Delete row',
    Icon: ArrowUp,
    run: (editor) => {
      editor.chain().focus().deleteRow().run()
    },
    destructive: true,
  },
  {
    key: 'delete-column',
    label: 'Delete column',
    Icon: ArrowLeft,
    run: (editor) => {
      editor.chain().focus().deleteColumn().run()
    },
    destructive: true,
  },
  {
    key: 'delete-table',
    label: 'Delete table',
    Icon: Trash2,
    run: (editor) => {
      editor.chain().focus().deleteTable().run()
    },
    destructive: true,
  },
]

function ToolbarButton({
  action,
  editor,
}: {
  action: TableAction
  editor: Editor
}) {
  const { label, Icon, run, destructive } = action

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={(event) => {
        event.preventDefault()
      }}
      onClick={() => {
        run(editor)
      }}
      className={
        'flex h-8 w-8 items-center justify-center rounded-md text-[13px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
        (destructive
          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 hover:text-red-700 dark:hover:text-red-300'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground')
      }
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}

export function TableToolbar({ editor }: TableToolbarProps) {
  return (
    <BubbleMenu
      editor={editor}
      pluginKey="tableToolbar"
      shouldShow={({ editor: ed, state }) =>
        ed.isEditable && ed.isActive('table') && state.selection.empty
      }
      className="flex items-center gap-1 rounded-lg border border-border/60 bg-card/98 px-2.5 py-2 text-sm shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur supports-[backdrop-filter]:backdrop-blur"
    >
      {ACTIONS.map((action) => (
        <ToolbarButton key={action.key} action={action} editor={editor} />
      ))}
      <div className="mx-0.5 h-5 w-px bg-border/60" />
      {DESTRUCTIVE_ACTIONS.map((action) => (
        <ToolbarButton key={action.key} action={action} editor={editor} />
      ))}
    </BubbleMenu>
  )
}
